/**
 * Pricing-page calculator helpers (PR 6h).
 *
 * Two pure functions, both fed by ``generatedPricing.ts``:
 *
 *   * ``estimateMonthlyPoints({ turns, tier })``
 *       Predicted points/month for a workload of ``turns`` calls per
 *       month at the given LLM tier. Uses the same rate sheet that
 *       bills the customer; numbers are honest, not aspirational.
 *
 *   * ``recommendPlanFor({ turns, tier })``
 *       Maps a workload estimate onto the smallest catalog plan whose
 *       ``points_per_period`` covers it. Returns the recommended plan
 *       PLUS a flag for the case where Pay-as-you-go would be cheaper.
 *       We surface that honestly; pretending PayG doesn't exist when
 *       it's the right answer erodes trust on the page where trust
 *       *is* the conversion lever.
 *
 * Per-turn token shape — locked here, documented for the calculator's
 * tooltip:
 *   * 1500 input tokens
 *   * 500 output tokens
 *
 * Why these numbers: average shape across the worker hot path
 * (system prompt + recent history + user turn vs. tool-call response).
 * Power users who care can read the math; everyone else gets a single
 * slider and a single tier toggle. Override available via the optional
 * ``shape`` arg if a future "advanced" form needs it.
 */
import {
  GENERATED_PLAN_CATALOG,
  MODEL_ROWS,
  UNLIMITED_BUDGET,
} from '../services/generatedPricing';

export const TYPICAL_TURN_SHAPE = {
  inputTokens: 1500,
  outputTokens: 500,
};

const _CAT = GENERATED_PLAN_CATALOG;

/**
 * @typedef {'fast' | 'think' | 'reason'} TierKey
 */

const _TIER_TO_FAMILY = {
  fast: 'haiku',
  think: 'sonnet',
  reason: 'opus',
};

function _modelRowFor(tier) {
  const family = _TIER_TO_FAMILY[tier];
  return family ? MODEL_ROWS[family] : null;
}

/**
 * Compute estimated points/month for a workload.
 *
 * @param {{ turns: number, tier: TierKey, shape?: { inputTokens: number, outputTokens: number } }} args
 * @returns {number} estimated points/month (integer)
 */
export function estimateMonthlyPoints({ turns, tier, shape } = {}) {
  const t = Math.max(0, Number.isFinite(turns) ? Math.round(turns) : 0);
  if (t === 0) return 0;
  const row = _modelRowFor(tier);
  if (!row) return 0;
  const s = shape ?? TYPICAL_TURN_SHAPE;
  const perTurn = row.pts_input * s.inputTokens + row.pts_output * s.outputTokens;
  return Math.round(t * perTurn);
}

/**
 * Compute how many turns of a given tier a plan's monthly budget covers.
 *
 * @param {string} planKey
 * @param {TierKey} tier
 * @returns {number | null} integer turns, or null when plan is unlimited / unknown / contact-only
 */
export function turnsCoveredAt(planKey, tier) {
  const plan = _CAT.find((p) => p.key === (planKey || '').toLowerCase());
  if (!plan || plan.contact_only) return null;
  if (plan.points_per_period === UNLIMITED_BUDGET) return Infinity;
  const row = _modelRowFor(tier);
  if (!row) return null;
  const perTurn = row.pts_input * TYPICAL_TURN_SHAPE.inputTokens
    + row.pts_output * TYPICAL_TURN_SHAPE.outputTokens;
  if (perTurn <= 0) return null;
  return Math.floor(plan.points_per_period / perTurn);
}

/**
 * @typedef {object} Recommendation
 * @property {object} plan - the recommended plan object from GENERATED_PLAN_CATALOG
 * @property {number} estimatedPoints - the estimate the recommendation is based on
 * @property {boolean} paygCheaper - true when PayG ($0.001/pt) would be
 *   cheaper than the recommended monthly subscription at this estimate
 * @property {boolean} overflowsAllFinite - true when even Ultra doesn't
 *   cover the estimate; recommendation is then "talk to us / go PayG"
 */

/**
 * Pick the smallest finite-budget plan whose monthly points cover the
 * estimated workload. Honesty rules:
 *
 *   1. If Pay-as-you-go ($1 / 1k points) would total less than the
 *      recommended subscription's price, flag it. Don't hide it.
 *   2. If even the top finite plan (Ultra) is exceeded, recommend
 *      Ultra anyway but flag overflow so the page can surface a
 *      "talk to us" or "consider PayG" prompt.
 *
 * Default fallback (zero turns / unknown tier / corrupt catalog):
 * the catalog's ``highlight=true`` plan (Pro by spec).
 *
 * @param {{ turns: number, tier: TierKey }} args
 * @returns {Recommendation}
 */
export function recommendPlanFor({ turns, tier } = {}) {
  const estimatedPoints = estimateMonthlyPoints({ turns, tier });

  const finite = _CAT
    .filter((p) => !p.contact_only && p.points_per_period !== UNLIMITED_BUDGET && p.points_per_period > 0)
    .sort((a, b) => a.points_per_period - b.points_per_period);

  const fallback = _CAT.find((p) => p.highlight) || finite[0] || _CAT[0];

  if (estimatedPoints <= 0 || finite.length === 0) {
    return {
      plan: fallback,
      estimatedPoints,
      paygCheaper: false,
      overflowsAllFinite: false,
    };
  }

  const fitting = finite.find((p) => p.points_per_period >= estimatedPoints);
  const overflowsAllFinite = !fitting;
  const recommended = fitting ?? finite[finite.length - 1];

  // PayG comparison: $1 per 1k points = price_cents 100 per 1000 points.
  // Recommended plan's price for the same volume:
  const recommendedCents = recommended.price_cents ?? 0;
  // PayG bills exactly the points consumed, no overage padding. So:
  const paygCents = Math.round((estimatedPoints / 1000) * 100);
  const paygCheaper = paygCents < recommendedCents;

  return {
    plan: recommended,
    estimatedPoints,
    paygCheaper,
    overflowsAllFinite,
  };
}
